import { type FC, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Link, generatePath } from "react-router-dom";

import { Badge } from "@mantine/core";
import { useIntersection } from "@mantine/hooks";
import { ShowIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { AbsoluteRoute } from "../../core/routes";
import { formatNumbers } from "../../lens-stats/formatters/formatNumbers";

import {
    lensIcon,
    lensInfoOverlay,
    lensName as lensNameClass,
    videoCard,
    videoPlayer,
    videoUnavailable,
    viewCountBadge,
    viewCountOverlay,
} from "./HomeMadeWithLensesSection.module.css";
import { MadeWithYourLensesVideoUnavailable, messages } from "./messages.generated";

const SPOTLIGHT_URL_PREFIX = "https://www.snapchat.com/spotlight/";

export interface SpotlightStoryCardProps {
    storySnapId: string;
    mediaUrl: string;
    views: number;
    lensName?: string;
    lensIconUrl?: string;
    lensId?: string;
    orgId?: string;
    lensFolderId?: string;
    onError: (storySnapId: string) => void;
    hasFailed: boolean;
}

export const SpotlightStoryCard: FC<SpotlightStoryCardProps> = memo(
    ({ storySnapId, mediaUrl, views, lensName, lensIconUrl, lensId, orgId, lensFolderId, onError, hasFailed }) => {
        const intl = useIntl();
        const videoRef = useRef<HTMLVideoElement>(null);
        const { ref: observerRef, entry } = useIntersection<HTMLDivElement>({ threshold: 0.5 });

        const handleError = useCallback(() => {
            onError(storySnapId);
        }, [onError, storySnapId]);

        const lensDetailPath = useMemo(() => {
            if (!lensId) return;
            return orgId && lensFolderId
                ? generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENS, { orgId, lensFolderId, lensId })
                : generatePath(AbsoluteRoute.LENS, { lensId });
        }, [lensId, orgId, lensFolderId]);

        // Sync video playback with viewport visibility
        useEffect(() => {
            const video = videoRef.current;
            if (!video) return;
            if (entry?.isIntersecting) {
                video.play().catch(() => {
                    // Swallow autoplay-blocked rejections
                });
            } else {
                video.pause();
            }
        }, [entry?.isIntersecting]);

        return (
            <div className={videoCard} ref={observerRef}>
                <a
                    href={`${SPOTLIGHT_URL_PREFIX}${storySnapId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={videoPlayer}
                    aria-label={intl.formatMessage(messages.madeWithYourLensesViewOnSpotlight)}
                >
                    {mediaUrl && !hasFailed ? (
                        <video ref={videoRef} muted loop width="100%" height="auto" onError={handleError}>
                            <source src={mediaUrl} />
                        </video>
                    ) : (
                        <div className={videoUnavailable}>
                            <MadeWithYourLensesVideoUnavailable />
                        </div>
                    )}
                </a>
                {lensName && lensDetailPath && (
                    <Link to={lensDetailPath} className={lensInfoOverlay}>
                        {lensIconUrl && <img src={lensIconUrl} alt={lensName} className={lensIcon} />}
                        <span className={lensNameClass}>{lensName}</span>
                    </Link>
                )}
                <div className={viewCountOverlay}>
                    <Badge
                        size="md"
                        radius="sm"
                        variant="filled"
                        color="dark"
                        leftSection={<ShowIcon width={14} height={14} fill="#FFFFFF" />}
                        className={viewCountBadge}
                    >
                        {formatNumbers(views)}
                    </Badge>
                </div>
            </div>
        );
    },
);
